import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export type StatusPlanoAcao = 'aberto' | 'em_andamento' | 'concluido' | 'cancelado';

export interface PlanoAcaoComDetalhes {
  id: string;
  titulo: string;
  status: StatusPlanoAcao;
  prioridade?: 'baixa' | 'media' | 'alta' | 'critica' | null;
  prazo_conclusao?: string | null;
  prazo_inicio?: string | null;
  local?: string | null;
  justificativa?: string | null;
  como_fazer?: string | null;
  custo_estimado?: number | null;
  resultado_obtido?: string | null;
  observacoes?: string | null;
  criado_em: string;
  atualizado_em: string;
  criado_por?: string | null;
  concluido_em?: string | null;
  concluido_por?: string | null;
  responsavel_id?: string | null;
  campo_id: string;
  resposta_id: string;
  formulario_campo_resposta_id?: string | null;
  categoria_id?: string | null;
  anexos?: any;
  responsavel?: { id: string; name: string; email?: string } | null;
  criador?: { id: string; name: string } | null;
  categoria?: { id: string; nome: string } | null;
  avaliacao_gut?: Array<{
    gravidade: number;
    urgencia: number;
    tendencia: number;
    score_gut: number | null;
    prioridade: string | null;
  }>;
  acoes?: Array<{
    id: string;
    status: string;
    percentual_conclusao: number | null;
  }>;
}

export function usePlanoAcaoResponsavel() {
  const { user } = useAuth();
  
  const { data: planosAcao, isLoading, refetch } = useQuery({
    queryKey: ['planos-acao-responsavel', user?.id],
    queryFn: async () => {
      // 1. Buscar grupos do usuário
      const { data: userGroups } = await supabase
        .from('user_groups')
        .select('group_id')
        .eq('user_id', user!.id);
      
      const groupIds = userGroups?.map(ug => ug.group_id) || [];
      
      // 2. Buscar categorias que pertencem a esses grupos
      let categoriaIds: string[] = [];
      if (groupIds.length > 0) {
        const { data: categorias } = await supabase
          .from('categorias_servico')
          .select('id')
          .in('grupo_id', groupIds);
        categoriaIds = categorias?.map(c => c.id) || [];
      }
      
      // 3. Query principal com .or() combinando responsavel_id e categoria_id
      let orFilter = `responsavel_id.eq.${user!.id}`;
      if (categoriaIds.length > 0) {
        orFilter += `,categoria_id.in.(${categoriaIds.join(',')})`;
      }
      
      const { data, error } = await supabase
        .from('formulario_planos_acao')
        .select(`
          *,
          responsavel:users!responsavel_id(id, name, email),
          criador:users!criado_por(id, name),
          categoria:categorias_servico!categoria_id(id, nome),
          avaliacao_gut(gravidade, urgencia, tendencia, score_gut, prioridade),
          acoes:acoes_5w2h(id, status, percentual_conclusao)
        `)
        .or(orFilter)
        .order('criado_em', { ascending: false });
      
      if (error) throw error;
      return data as PlanoAcaoComDetalhes[];
    },
    enabled: !!user?.id,
  });
  
  // Calcular progresso do plano baseado nas ações
  const calcularProgressoPlano = (plano: PlanoAcaoComDetalhes) => {
    const acoes = plano.acoes || [];
    if (acoes.length === 0) return 0;
    const totalProgresso = acoes.reduce((acc, acao) => 
      acc + (acao.percentual_conclusao || 0), 0
    );
    return Math.round(totalProgresso / acoes.length);
  };
  
  // Calcular métricas
  const metricas = {
    total: planosAcao?.length || 0,
    abertos: planosAcao?.filter(p => p.status === 'aberto').length || 0,
    emAndamento: planosAcao?.filter(p => p.status === 'em_andamento').length || 0,
    concluidos: planosAcao?.filter(p => p.status === 'concluido').length || 0,
    cancelados: planosAcao?.filter(p => p.status === 'cancelado').length || 0,
    atrasados: planosAcao?.filter(p => 
      p.status !== 'concluido' && 
      p.prazo_conclusao && 
      new Date(p.prazo_conclusao) < new Date()
    ).length || 0,
    proximoVencimento: planosAcao?.filter(p => 
      p.status !== 'concluido' && 
      p.prazo_conclusao && 
      new Date(p.prazo_conclusao) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) &&
      new Date(p.prazo_conclusao) >= new Date()
    ).length || 0,
    porPrioridadeGUT: {
      critica: planosAcao?.filter(p => p.avaliacao_gut?.[0]?.prioridade === 'critica').length || 0,
      alta: planosAcao?.filter(p => p.avaliacao_gut?.[0]?.prioridade === 'alta').length || 0,
      media: planosAcao?.filter(p => p.avaliacao_gut?.[0]?.prioridade === 'media').length || 0,
      baixa: planosAcao?.filter(p => p.avaliacao_gut?.[0]?.prioridade === 'baixa').length || 0,
    },
    totalAcoes: planosAcao?.reduce((acc, p) => acc + (p.acoes?.length || 0), 0) || 0,
    acoesConcluidas: planosAcao?.reduce((acc, p) => 
      acc + (p.acoes?.filter(a => a.status === 'concluida').length || 0), 0
    ) || 0,
  };
  
  return {
    planosAcao,
    metricas,
    isLoading,
    refetch,
    calcularProgressoPlano,
  };
}
